import React, { useState, useEffect } from "react";
import { cityActivity as texts_es } from "../../../utils/text/graphTexts";
import { cityActivity as texts_en } from "../../../utils/text/graphTexts_en";
import { useParams } from "react-router-dom";
import { COUNTRY_OPTIONS } from "../../../utils/constants/countryOptions";
import CardContainer from "../../reusable/CardContainer";
import CenterSpin from "../../misc/spin";
import CardOnlyYear from "../../reusable/CardOnlyYear";
import { getDateString } from "../../../utils/functions/getDateString";

import {
  getCityActivityRatio,
  getCityActivityVolume,
} from "../../../services/dashboard";
import { getGeoJSON } from "../../../services/jsons";
import "./ActivityByCity.css";
import { useContextData } from "../../../hooks/context";
import { useCurrentLanguage } from "../../../hooks/useCurrentLanguage";
import CardError from "../../reusable/CardError/CardError";
import miscText from "../../../utils/text/misc.json";
import { getGraphFileName } from "../../../utils/functions/getGraphFileName";
import { getDownloadSubtitle } from "../../../utils/functions/getDownloadSubtitle";
import { getMonthText } from "../../../utils/functions/getMonthText";

export default function ActivityByCity({ selectedCountry }) {
  const { country } = useParams();
  const { dateOptions } = useContextData();
  const { language } = useCurrentLanguage();

  // State
  const [currentCountry, setCurrentCountry] = useState(
    selectedCountry ?? country
  );
  const [currentCardType] = useState("activity-by-city");
  const [texts, setTexts] = useState(language === "en" ? texts_en : texts_es);
  const [countryProps, setCountryProps] = useState(
    COUNTRY_OPTIONS.find(
      (opt) => opt.code === selectedCountry || opt.code === country
    )
  );
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorLoadingGraph, setErrorLoadingGraph] = useState(false);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [selectedYear, setSelectedYear] = useState();
  const [selectedQuarter, setSelectedQuarter] = useState();

  // Unique to PrincipalTopics component
  // Never send unique states to Card components
  const [mostActiveCity, setMostActiveCity] = useState();
  const [isMapJsonReady, setIsMapJsonReady] = useState(false);
  const [mapData, setMapData] = useState();
  const [geoJSONData, setGeoJSONData] = useState();
  const [selectedDataType, setSelectedDataType] = useState("all");

  const handleYearChange = (newSelectedYear) => {
    setSelectedYear(newSelectedYear);
  };
  const handleQuarterChange = (newSelectedQuarter) => {
    setSelectedQuarter(newSelectedQuarter);
  };
  const handleXenoSelectedChange = (newIsXenoSelectedValue) => {
    setSelectedDataType(newIsXenoSelectedValue);
  };

  // Useeffect that collents yead and quarter data
  useEffect(() => {
    if (dateOptions !== null) {
      if (dateOptions && dateOptions.mostRecentQuarterDate) {
        setSelectedYear(dateOptions.mostRecentQuarterDate.year);
        setSelectedQuarter(dateOptions.mostRecentQuarterDate.quarter);
      }
      setIsLoadingInitialData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions]);

  // useEffect to load data for graph and creates Echarts options
  // Listend to dateOptions and selected year
  // Only funs when these params are not null
  useEffect(() => {
    const updateData = async () => {
      try {
        setIsLoading(true);

        let responseData;
        switch (selectedDataType) {
          case "all":
            responseData = await getCityActivityRatio(
              currentCountry,
              selectedYear,
              selectedQuarter,
              false
            );
            break;
          case "xeno":
            responseData = await getCityActivityRatio(
              currentCountry,
              selectedYear,
              selectedQuarter,
              true
            );
            break;
          case "all_volume":
            responseData = await getCityActivityVolume(
              currentCountry,
              selectedYear,
              selectedQuarter,
              false
            );
            break;
          case "xeno_volume":
            responseData = await getCityActivityVolume(
              currentCountry,
              selectedYear,
              selectedQuarter,
              true
            );
            break;
          default:
            responseData = {};
        }

        responseData.data.points.sort((a, b) => b.value - a.value);

        if (responseData.data.points.length === 0) setMostActiveCity("--");
        else setMostActiveCity(responseData.data.points[0].metro_zone);

        setMapData(responseData.data);

        if (responseData.data.points.length === 0) setIsDataAvailable(false);
        else setIsDataAvailable(true);
        setIsLoading(false);
        setErrorLoadingGraph(false);
      } catch (error) {
        console.log("caught");
        setErrorLoadingGraph(true);
        setMapData(null);
        setMostActiveCity(null);
        setIsDataAvailable(false);
        setIsLoading(false);
      }
    };

    if (!isLoadingInitialData && dateOptions && selectedYear) updateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateOptions,
    selectedYear,
    selectedQuarter,
    selectedDataType,
    isLoadingInitialData,
    currentCountry,
  ]);

  //map
  useEffect(() => {
    async function getMapShapeFile() {
      setIsMapJsonReady(false);
      const responseMapGeoJSON = await getGeoJSON(currentCountry);
      setGeoJSONData(responseMapGeoJSON);
      setIsMapJsonReady(true);
    }

    try {
      setGeoJSONData(null);
      getMapShapeFile();
    } catch (error) {
      setIsMapJsonReady(false);
      console.log("Error loading country's shapefile", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCountry]);

  const xenofOptions = [
    { xenof_list: "Todos los tweets", xenof_code: "all_volume" },
    { xenof_list: "Todos los tweets (tasa)", xenof_code: "all" },
    { xenof_list: "Tweets xenófobos", xenof_code: "xeno_volume" },
    { xenof_list: "Tweets xenófobos (tasa)", xenof_code: "xeno" },
  ];

  useEffect(() => {
    if (selectedCountry) {
      setCurrentCountry(selectedCountry);
      setCountryProps(
        COUNTRY_OPTIONS.find((opt) => opt.code === selectedCountry)
      );
    }
  }, [selectedCountry]);

  useEffect(() => {
    setTexts(language === "en" ? texts_en : texts_es);
  }, [language]);

  function ActivityCardContent() {
    let content;
    try {
      content = (
        <CardOnlyYear
          countryProps={countryProps}
          geoJSONData={geoJSONData}
          selectedDataType={selectedDataType}
          mapData={mapData}
          isMapJsonReady={isMapJsonReady}
          dateOptions={dateOptions}
          isLoading={isLoading}
          errorLoading={errorLoadingGraph}
          isDataAvailable={isDataAvailable}
          lastMonthData={mostActiveCity}
          dataDate={getDateString(
            language,
            selectedYear,
            selectedQuarter === "ALL" ? null : selectedQuarter
          )}
          cardTitle={texts.boxTitle}
          methodology={texts.methodology()}
          dataSubtitle={texts.dataSubtitle(selectedDataType)}
          description={texts.description}
          selectArray={[
            {
              selectedValue: selectedYear,
              changeHandler: handleYearChange,
              options: dateOptions.yearOptions.filter(
                (year) => year.years_code !== "HIST"
              ),
              placeHolderText: miscText["yearPlaceholder"][language],
              labelAttributeName: "years_list",
              valueAttributeName: "years_code",
            },
            {
              selectedValue: selectedQuarter,
              changeHandler: handleQuarterChange,
              options: dateOptions.quarterOptions[selectedYear],
              placeHolderText: miscText["quarterPlaceholder"][language],
              labelAttributeName: "label",
              valueAttributeName: "value",
              ignoreTranslation: true,
            },
            {
              selectedValue: selectedDataType,
              changeHandler: handleXenoSelectedChange,
              options: xenofOptions,
              placeHolderText: miscText["typePlaceholder"][language],
              labelAttributeName: "xenof_list",
              valueAttributeName: "xenof_code",
            },
          ]}
          language={language}
          downloadableCardID={currentCardType}
          downloadableCardFileName={getGraphFileName(
            language === "es" ? "ActividadPorCiudad" : "ActivityByCity",
            currentCountry,
            selectedYear,
            selectedQuarter
          )}
          downloadableCardTitle={texts.boxTitle}
          downloadableCardSubtitle={getDownloadSubtitle(
            countryProps,
            selectedYear,
            language,
            getMonthText(selectedQuarter, language)
          )}
          downloadableCardContainsLegend={currentCountry === "LAC"}
          hideDownload={true}
        />
      );
    } catch (error) {
      content = (
        <CardError
          cardTitle={texts.boxTitle}
          methodology={texts.methodology()}
          dataSubtitle={texts.dataSubtitle("all")}
          description={texts.description}
          selectArray={[
            {
              placeHolderText: miscText["yearPlaceholder"][language],
              language: language,
            },
            {
              placeHolderText: miscText["quarterPlaceholder"][language],
              language: language,
            },
            {
              placeHolderText: miscText["typePlaceholder"][language],
              language: language,
            },
          ]}
        />
      );
    }

    return content;
  }

  return selectedCountry !== "LAC" ? (
    <CardContainer>
      {isLoadingInitialData ? <CenterSpin /> : <ActivityCardContent />}
    </CardContainer>
  ) : null;
}
